import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { analyticsAPI, plansAPI, employerAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CompanyPageCard } from '@/components/CompanyPageCard';
import { LoyaltyCard } from '@/components/LoyaltyCard';
import { FiscalDataCard } from '@/components/FiscalDataCard';
import { PioneerCountdownBanner } from '@/components/PioneerCountdownBanner';
import { NifNoticeBanner } from '@/components/NifNoticeBanner';
import { Briefcase, Eye, TrendingUp, Star, PlusCircle, Wallet, Clock, ShoppingCart, ShieldCheck, Crown, ScrollText, Users, FileText, Tag, CalendarClock } from 'lucide-react';
import axios from 'axios';

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nifValue, setNifValue] = useState('');
  const [nifSaving, setNifSaving] = useState(false);
  const [nifDismissed, setNifDismissed] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryValue, setCategoryValue] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryDismissed, setCategoryDismissed] = useState(() => {
    try { return sessionStorage.getItem('categoryPromptDismissed') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    loadDashboard();
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then((r) => setCategories(r.data.items || []))
      .catch(() => {});
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, creditsRes] = await Promise.all([
        analyticsAPI.getDashboard().catch(() => ({ data: null })),
        plansAPI.getMyCredits().catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setCredits(creditsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Briefcase,  label: t('dashboard.stats.totalJobs'),      value: stats?.total_jobs || 0,            color: 'blue',   testId: 'stat-total-jobs' },
    { icon: TrendingUp, label: t('dashboard.stats.activeJobs'),     value: stats?.active_jobs || 0,           color: 'green',  testId: 'stat-active-jobs' },
    { icon: Eye,        label: t('dashboard.stats.totalViews'),     value: stats?.total_views || 0,           color: 'purple', testId: 'stat-total-views' },
    { icon: Users,      label: t('dashboard.stats.applications'),   value: stats?.total_applications || 0,   color: 'orange', testId: 'stat-total-applications' }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('pt-PT');
    } catch {
      return '—';
    }
  };

  const BadgeVisual = ({ type }) => {
    if (type === 'premium') {
      return <Badge className="bg-purple-600 hover:bg-purple-700"><Crown className="h-3 w-3 mr-1" />{t('dashboard.badgePremium')}</Badge>;
    }
    if (type === 'verified') {
      return <Badge className="bg-orange-500 hover:bg-orange-600"><ShieldCheck className="h-3 w-3 mr-1" />{t('dashboard.badgeVerified')}</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalCredits = credits?.total_credits || 0;
  const featuredCredits = credits?.featured_credits || 0;
  const hasFree = credits?.has_free_available;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('dashboard.welcome')}, {(user?.company_name || '').replace(/\b\w/g, c => c.toUpperCase())}!
            </h1>
            <p className="text-gray-600 mt-2">{t('dashboard.subtitle')}</p>
            {credits?.badge && (
              <div className="mt-3"><BadgeVisual type={credits.badge} /></div>
            )}
          </div>
          <Button
            onClick={() => navigate('/checkout')}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="dashboard-buy-credits-btn"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {t('dashboard.buyMoreAds')}
          </Button>
        </div>
        {user?.role === 'employer' && <NifNoticeBanner user={user} />}
        <PioneerCountdownBanner credits={credits} />

        {/* NIF prompt — encourage employer to add fiscal NIF for invoices */}
        {user?.role !== 'employer' && !user?.fiscal_nif && !nifDismissed && (
          <Card className="p-5 mb-6 border-amber-300 bg-amber-50" data-testid="nif-prompt-card">
            <div className="flex items-start gap-3 flex-wrap">
              <FileText className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 sm:min-w-[240px]">
                <p className="font-semibold text-amber-900">{t('dashboard.nifPromptTitle')}</p>
                <p className="text-sm text-amber-800 mb-3">
                  {t('dashboard.nifPromptBody')}
                </p>
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!nifValue.trim()) return;
                    setNifSaving(true);
                    try {
                      const { data } = await employerAPI.updateProfile({ fiscal_nif: nifValue.trim() });
                      setUser(data);
                    } catch (err) {
                      console.error('Failed to save NIF', err);
                    } finally {
                      setNifSaving(false);
                    }
                  }}
                >
                  <Input
                    placeholder="Ex: 123456789"
                    value={nifValue}
                    onChange={(e) => setNifValue(e.target.value)}
                    className="w-44 bg-white"
                    data-testid="nif-prompt-input"
                  />
                  <Button
                    type="submit"
                    disabled={nifSaving || !nifValue.trim()}
                    className="bg-amber-700 hover:bg-amber-800"
                    data-testid="nif-prompt-save-btn"
                  >
                    {nifSaving ? t('dashboard.saving') : t('dashboard.saveNif')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setNifDismissed(true)}
                    data-testid="nif-prompt-dismiss-btn"
                  >
                    {t('dashboard.later')}
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        )}

        {/* Business category prompt — encourage employer to pick sector for job suggestions */}
        {user?.role !== 'admin' && !user?.business_category && !categoryDismissed && (
          <Card className="p-5 mb-6 border-blue-300 bg-blue-50" data-testid="category-prompt-card">
            <div className="flex items-start gap-3 flex-wrap">
              <Tag className="h-5 w-5 text-blue-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 sm:min-w-[240px]">
                <p className="font-semibold text-blue-900">{t('dashboard.categoryPromptTitle')}</p>
                <p className="text-sm text-blue-800 mb-3">
                  {t('dashboard.categoryPromptBody')}
                </p>
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!categoryValue) return;
                    setCategorySaving(true);
                    try {
                      const { data } = await employerAPI.updateProfile({ business_category: categoryValue });
                      setUser(data);
                    } catch (err) {
                      console.error('Failed to save category', err);
                    } finally {
                      setCategorySaving(false);
                    }
                  }}
                >
                  <select
                    value={categoryValue}
                    onChange={(e) => setCategoryValue(e.target.value)}
                    className="flex h-10 rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 w-full sm:min-w-[240px]"
                    data-testid="category-prompt-select"
                  >
                    <option value="">{t('dashboard.chooseCategory')}</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name_pt}</option>
                    ))}
                  </select>
                  <Button
                    type="submit"
                    disabled={categorySaving || !categoryValue}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="category-prompt-save-btn"
                  >
                    {categorySaving ? t('dashboard.saving') : t('dashboard.saveSector')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      try { sessionStorage.setItem('categoryPromptDismissed', '1'); } catch { /* ignore */ }
                      setCategoryDismissed(true);
                    }}
                    data-testid="category-prompt-dismiss-btn"
                  >
                    {t('dashboard.later')}
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        )}

        {/* Multi-sector management — always visible for employers who already
            picked at least one sector. Lets Grupo Tavares style companies
            operate across Construção + Digital + Hotelaria simultaneously. */}
        {user?.role !== 'admin' && user?.business_category && (
          <MultiSectorCard user={user} setUser={setUser} categories={categories} />
        )}

        <CompanyPageCard user={user} />

        {user?.role === 'employer' && <FiscalDataCard user={user} setUser={setUser} />}

        {/* Credits Card */}
        <Card className="p-6 mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="credits-card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <Wallet className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">{t('dashboard.creditsRemaining')}</span>
              </div>
              <p className="text-4xl font-bold text-gray-900" data-testid="credits-total">{credits?.unlimited ? 'Ilimitadas' : totalCredits}</p>
              <p className="text-xs text-gray-600 mt-1">{t('dashboard.creditsRemainingHint')}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <Star className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">{t('dashboard.featured')}</span>
              </div>
              <p className="text-4xl font-bold text-gray-900" data-testid="credits-featured">{credits?.unlimited ? 'Ilimitadas' : featuredCredits}</p>
              <p className="text-xs text-gray-600 mt-1">{t('dashboard.featuredHint')}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">{t('dashboard.expiry')}</span>
              </div>
              <p className="text-xl font-bold text-gray-900" data-testid="credits-expiry">
                {credits?.earliest_expiry ? formatDate(credits.earliest_expiry) : t('dashboard.noActivePack')}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {credits?.earliest_expiry ? t('dashboard.expiryHint') : '—'}
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <CheckIcon />
                <span className="text-sm font-medium uppercase tracking-wide">{t('dashboard.free')}</span>
              </div>
              <p className="text-xl font-bold text-gray-900" data-testid="credits-free-available">
                {hasFree ? t('dashboard.available') : t('dashboard.exhausted')}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {hasFree ? t('dashboard.freeAvailableHint') : t('dashboard.freeExhaustedHint')}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/checkout')}
              data-testid="credits-buy-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />{t('dashboard.buyPack')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/purchases')}
              data-testid="credits-history-btn"
            >
              <ScrollText className="h-4 w-4 mr-2" />{t('dashboard.purchaseHistory')}
            </Button>
          </div>
        </Card>

        <LoyaltyCard />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Card key={index} className="p-6" data-testid={stat.testId}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-8 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/create-job')} data-testid="action-create-job">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-blue-100 rounded-lg">
                <PlusCircle className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{t('dashboard.createJob')}</h3>
                <p className="text-gray-600">{t('dashboard.createJobHint')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-8 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/my-jobs')} data-testid="action-my-jobs">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-green-100 rounded-lg">
                <Briefcase className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{t('nav.myJobs')}</h3>
                <p className="text-gray-600">{t('dashboard.myJobsHint')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-8 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/applications')} data-testid="action-applications">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-purple-100 rounded-lg">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{t('dashboard.applicationsReceived')}</h3>
                <p className="text-gray-600">{t('dashboard.applicationsReceivedHint')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-8 hover:shadow-lg transition-shadow cursor-pointer border-blue-200" onClick={() => navigate('/interviews')} data-testid="action-interviews">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-blue-100 rounded-lg">
                <CalendarClock className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{t('dashboard.interviews')}</h3>
                <p className="text-gray-600">{t('dashboard.interviewsHint')}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Empty State */}
        {stats && stats.total_jobs === 0 && (
          <Card className="mt-8 p-12 text-center">
            <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('dashboard.emptyTitle')}</h3>
            <p className="text-gray-600 mb-6">{t('dashboard.emptyBody')}</p>
            <Button onClick={() => navigate('/create-job')} className="bg-blue-600 hover:bg-blue-700">
              <PlusCircle className="h-4 w-4 mr-2" />{t('dashboard.emptyCta')}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}


function MultiSectorCard({ user, setUser, categories }) {
  const { t } = useTranslation();
  // Initial selection: prefer the new array; fall back to the legacy scalar.
  const initial = React.useMemo(() => {
    if (Array.isArray(user?.business_categories) && user.business_categories.length > 0) {
      return user.business_categories;
    }
    return user?.business_category ? [user.business_category] : [];
  }, [user?.business_categories, user?.business_category]);

  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setSelected(initial); }, [initial]);

  const toggle = (slug) => {
    setSelected((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
  };

  const dirty = React.useMemo(() => {
    if (selected.length !== initial.length) return true;
    const a = [...selected].sort().join(',');
    const b = [...initial].sort().join(',');
    return a !== b;
  }, [selected, initial]);

  const save = async () => {
    if (selected.length === 0) {
      alert(t('dashboard.chooseAtLeastOneSector'));
      return;
    }
    setSaving(true);
    try {
      const { data } = await employerAPI.updateProfile({ business_categories: selected });
      setUser(data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      alert(e?.response?.data?.detail || t('dashboard.errorSaveSectors'));
    } finally { setSaving(false); }
  };

  if (!categories || categories.length === 0) return null;

  return (
    <Card className="p-5 mb-6 border-slate-200" data-testid="multi-sector-card">
      <div className="flex items-start gap-3 flex-wrap">
        <Tag className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0 sm:min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-900">{t('dashboard.multiSectorTitle')}</p>
            {savedFlash && (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full" data-testid="multi-sector-saved">{t('dashboard.saved')} ✓</span>
            )}
          </div>
          <p className="text-sm text-slate-600 mb-3">
            {t('dashboard.multiSectorBody')}
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((c) => {
              const active = selected.includes(c.slug);
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => toggle(c.slug)}
                  className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-all ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:text-blue-700'
                  }`}
                  data-testid={`multi-sector-pill-${c.slug}`}
                >
                  {c.name_pt}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="multi-sector-save-btn"
            >
              {saving ? t('dashboard.saving2') : t('dashboard.saveSectors')}
            </Button>
            <span className="text-xs text-slate-500" data-testid="multi-sector-count">
              {t('dashboard.sectorsSelected', { count: selected.length })}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
